/**
 * The page's view as URL search parameters (SW-8879): the span, the day it
 * ends on, and the filters. Everything the page shows derives from this, so a
 * link carries the whole picture and the browser's Back works.
 */
import { shiftDay, todayIso } from "./time";

export type Span = "day" | "7d" | "30d" | "custom";

export interface FleetQuery {
  span: Span;
  /** The last day of the span, YYYY-MM-DD. */
  to: string;
  /** The first day, custom spans only. */
  from?: string;
  customer?: string;
  allocation?: string;
  rigs: boolean;
}

const SPANS: Span[] = ["day", "7d", "30d", "custom"];
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

function isDay(value: string | null): value is string {
  if (!value || !DAY_RE.test(value)) return false;
  const parsed = new Date(`${value}T12:00:00Z`);
  return (
    !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(value)
  );
}

export function parseQuery(search: string): FleetQuery {
  const params = new URLSearchParams(search);
  const rawSpan = params.get("span");
  const span = SPANS.includes(rawSpan as Span) ? (rawSpan as Span) : "7d";
  const to = isDay(params.get("to"))
    ? (params.get("to") as string)
    : todayIso();
  const out: FleetQuery = { span, to, rigs: params.get("rigs") === "true" };
  const from = params.get("from");
  if (span === "custom" && isDay(from)) out.from = from;
  const customer = params.get("customer");
  if (customer) out.customer = customer;
  const allocation = params.get("allocation");
  if (allocation) out.allocation = allocation;
  return out;
}

export function toSearch(q: FleetQuery): string {
  const params = new URLSearchParams();
  params.set("span", q.span);
  if (q.span === "custom" && q.from) params.set("from", q.from);
  params.set("to", q.to);
  if (q.customer) params.set("customer", q.customer);
  if (q.allocation) params.set("allocation", q.allocation);
  if (q.rigs) params.set("rigs", "true");
  return params.toString();
}

/** The first and last day the span covers. */
export function spanBounds(q: FleetQuery): { from: string; to: string } {
  switch (q.span) {
    case "day":
      return { from: q.to, to: q.to };
    case "30d":
      return { from: shiftDay(q.to, -29), to: q.to };
    case "custom":
      return {
        from: q.from && q.from <= q.to ? q.from : shiftDay(q.to, -6),
        to: q.to,
      };
    default:
      return { from: shiftDay(q.to, -6), to: q.to };
  }
}

/** The same span one step earlier or later; a day span steps a day. */
export function stepSpan(q: FleetQuery, direction: -1 | 1): FleetQuery {
  const { from, to } = spanBounds(q);
  const length =
    Math.round((Date.parse(to) - Date.parse(from)) / 86400_000) + 1;
  const nextTo = shiftDay(q.to, direction * length);
  const capped = nextTo > todayIso() ? todayIso() : nextTo;
  const next: FleetQuery = { ...q, to: capped };
  if (q.span === "custom") next.from = shiftDay(capped, -(length - 1));
  return next;
}
